// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: which icons the Rail's strip shows, and in what order. Kept on this
// computer (the Rail is a desktop thing) and shared between the main window,
// where Settings edits it, and the Rail window through the storage event.

import {RAIL_KEYS, type RailKey} from '@app/features/konvo_rail/state/RailState';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {makeAutoObservable} from 'mobx';

const STORAGE_KEY = 'konvo_rail.tabs';

export interface RailTabsValue {
	order: Array<RailKey>;
	hidden: Array<RailKey>;
}

/** Known keys only, each once, every key present in the order; at least one stays visible. */
export function sanitizeRailTabs(raw: unknown): RailTabsValue {
	const value = (raw && typeof raw === 'object' ? raw : {}) as {order?: unknown; hidden?: unknown};
	const known = (list: unknown): Array<RailKey> =>
		Array.isArray(list)
			? list.filter((key, index): key is RailKey => RAIL_KEYS.includes(key as RailKey) && list.indexOf(key) === index)
			: [];
	const order = known(value.order);
	for (const key of RAIL_KEYS) if (!order.includes(key)) order.push(key);
	let hidden = known(value.hidden);
	if (hidden.length >= RAIL_KEYS.length) hidden = [];
	return {order, hidden};
}

function read(): RailTabsValue {
	try {
		const raw = getProtectedLocalStorage()?.getItem(STORAGE_KEY);
		return sanitizeRailTabs(raw ? JSON.parse(raw) : null);
	} catch {
		return sanitizeRailTabs(null);
	}
}

class RailTabsStore {
	value: RailTabsValue = read();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		if (typeof window !== 'undefined') {
			window.addEventListener('storage', (event) => {
				if (event.key === STORAGE_KEY) this.value = read();
			});
		}
	}

	/** The strip's icons, in order, without the hidden ones. */
	get visible(): Array<RailKey> {
		return this.value.order.filter((key) => !this.value.hidden.includes(key));
	}

	isHidden(key: RailKey): boolean {
		return this.value.hidden.includes(key);
	}

	setHidden(key: RailKey, hidden: boolean): void {
		const next = hidden ? [...this.value.hidden, key] : this.value.hidden.filter((entry) => entry !== key);
		this.write({...this.value, hidden: next});
	}

	move(key: RailKey, delta: -1 | 1): void {
		const order = [...this.value.order];
		const from = order.indexOf(key);
		const to = from + delta;
		if (from < 0 || to < 0 || to >= order.length) return;
		order.splice(from, 1);
		order.splice(to, 0, key);
		this.write({...this.value, order});
	}

	reset(): void {
		this.write(sanitizeRailTabs(null));
	}

	private write(next: RailTabsValue): void {
		this.value = sanitizeRailTabs(next);
		try {
			getProtectedLocalStorage()?.setItem(STORAGE_KEY, JSON.stringify(this.value));
		} catch {
			// Storage may be unavailable; the change still holds for this window.
		}
	}
}

export default new RailTabsStore();
